import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Button,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusBadge from '../components/common/StatusBadge';
import StudentDetail from '../components/students/StudentDetail';
import StudentStatusStepper from '../components/students/StudentStatusStepper';
import StatusTransitionDialog from '../components/students/StatusTransitionDialog';
import DocumentCategoryGrid from '../components/documents/DocumentCategoryGrid';
import ActivityTimeline from '../components/activity/ActivityTimeline';
import AssignmentPanel from '../components/assignments/AssignmentPanel';
import ContactRemarks from '../components/students/ContactRemarks';
import PreviousLoans from '../components/students/PreviousLoans';
import ApplicationFormView from '../components/students/ApplicationFormView';
import CibilReportsSection from '../components/students/CibilReportsSection';
import usePermissions from '../hooks/usePermissions';
import useAuth from '../hooks/useAuth';
import {
  getStudentById,
  updateStudentStatus,
} from '../services/studentService';

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;
  return <Box sx={{ py: 3 }}>{children}</Box>;
};

const StudentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const { canEditStudent, isAdmin, isManager } = usePermissions();

  const [student, setStudent] = useState(null);
  const [applicationForm, setApplicationForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Status transition dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchStudent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getStudentById(id);
      setStudent(response.data?.data?.student || null);
      setApplicationForm(response.data?.data?.applicationForm || null);
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Failed to load student';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchStudent();
  }, [fetchStudent]);

  const handleStatusChange = async ({ status, notes }) => {
    setStatusUpdating(true);

    try {
      await updateStudentStatus(id, { status, notes });
      setStatusDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Status updated to "${status}" successfully`,
        severity: 'success',
      });
      // Refresh student data
      fetchStudent();
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Failed to update status';
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box>
        <PageHeader
          title="Student Detail"
          breadcrumbs={[
            { label: 'Dashboard', link: '/dashboard' },
            { label: 'Students', link: '/students' },
            { label: 'Detail' },
          ]}
        />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!student) return null;

  return (
    <Box>
      <PageHeader
        title={student.name}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Students', link: '/students' },
          { label: student.fileNumber || student.name },
        ]}
      >
        <StatusBadge status={student.status} />

        <Button
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          onClick={() => setStatusDialogOpen(true)}
        >
          Change Status
        </Button>

        {canEditStudent && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/students/${id}/edit`)}
          >
            Edit
          </Button>
        )}
      </PageHeader>

      {/* Status Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <StudentStatusStepper currentStatus={student.status} />
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" />
          <Tab label="Documents" />
          <Tab label="Timeline" />
          <Tab label="Remarks" />
          <Tab label="Loan History" />
          <Tab label="CIBIL Reports" />
          <Tab label="Loan Form" />
        </Tabs>

        <Box sx={{ px: 3 }}>
          {/* Overview Tab */}
          <TabPanel value={activeTab} index={0}>
            <StudentDetail student={student} onRefresh={fetchStudent} />
            {(isAdmin || isManager) && (
              <Box sx={{ mt: 3 }}>
                <AssignmentPanel student={student} onRefresh={fetchStudent} />
              </Box>
            )}
          </TabPanel>

          {/* Documents Tab */}
          <TabPanel value={activeTab} index={1}>
            <DocumentCategoryGrid studentId={id} />
          </TabPanel>

          {/* Timeline Tab */}
          <TabPanel value={activeTab} index={2}>
            <ActivityTimeline studentId={id} />
          </TabPanel>

          {/* Remarks Tab */}
          <TabPanel value={activeTab} index={3}>
            <ContactRemarks studentId={id} />
          </TabPanel>

          {/* Loan History Tab */}
          <TabPanel value={activeTab} index={4}>
            <PreviousLoans studentId={id} />
          </TabPanel>

          {/* CIBIL Reports Tab */}
          <TabPanel value={activeTab} index={5}>
            <CibilReportsSection studentId={id} student={student} onRefresh={fetchStudent} />
          </TabPanel>

          {/* Loan Form Tab */}
          <TabPanel value={activeTab} index={6}>
            <ApplicationFormView applicationForm={applicationForm} student={student} onRefresh={fetchStudent} />
          </TabPanel>
        </Box>
      </Paper>

      {/* Status Transition Dialog */}
      <StatusTransitionDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        currentStatus={student.status}
        onSubmit={handleStatusChange}
        userRole={role}
        loading={statusUpdating}
      />

      {/* Success / Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentDetailPage;
